// src/pages/ResetPassword.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import API from '../api';

function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [token, setToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [tokenError, setTokenError] = useState(false);

  useEffect(() => {
    // Recupera il token dalla query string
    const queryParams = new URLSearchParams(location.search);
    const t = queryParams.get('token');
    if (t) {
      setToken(t);
    } else {
      setMessage('Token mancante nella richiesta.');
      setTokenError(true);
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setIsLoading(true);

    if (newPassword !== confirmPassword) {
      setMessage('Le password non coincidono.');
      setIsLoading(false);
      return;
    }

    if (!token) {
      setMessage('Token mancante.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await API.post('/auth/reset-password', {
        token,
        newPassword
      });

      if (response.data.success) {
        setMessage('Password reimpostata con successo! Ora puoi effettuare il login.');
        // Reindirizza alla pagina di login dopo qualche secondo
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setMessage(response.data.message || 'Errore nella reimpostazione della password.');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Errore di rete.';
      setMessage(errorMessage);
      
      // Se il token è scaduto o non valido, mostra il link per richiedere un nuovo token
      if (errorMessage.includes('scaduto') || errorMessage.includes('non valido')) {
        setTokenError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="reset-password-page">
      <div className="reset-password-container">
        {/* Header con icona */}
        <div className="reset-password-header">
          <div className="reset-password-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="10,17 15,12 10,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="15" y1="12" x2="3" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 className="reset-password-title">Reimposta Password</h2>
          <p className="reset-password-subtitle">Inserisci la tua nuova password</p>
        </div>
        
        {tokenError ? (
          <div className="reset-password-warning">
            <h5>Token non valido o scaduto</h5>
            <p>Il link per il reset della password non è più valido. Puoi richiedere un nuovo link.</p>
            <Link to="/forgot-password" className="reset-password-submit-btn">
              Richiedi nuovo link
            </Link>
          </div>
        ) : (
          <>
            {message && (
              <div className={`${message.includes('successo') ? 'reset-password-success' : 'reset-password-error'}`}>
                <svg className={`${message.includes('successo') ? 'reset-password-success-icon' : 'reset-password-error-icon'}`} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {message.includes('successo') ? (
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  ) : (
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  )}
                  {message.includes('successo') ? (
                    <polyline points="22,4 12,14.01 9,11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  ) : (
                    <>
                      <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </>
                  )}
                </svg>
                {message}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="reset-password-form">
              <div className="reset-password-form-group">
                <label className="reset-password-form-label">Nuova Password</label>
                <div className="reset-password-input-wrapper">
                  <svg className="reset-password-input-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="16" r="1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <input
                    type="password"
                    className="reset-password-input"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Inserisci la nuova password"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>
              <div className="reset-password-form-group">
                <label className="reset-password-form-label">Conferma Password</label>
                <div className="reset-password-input-wrapper">
                  <svg className="reset-password-input-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="16" r="1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <input
                    type="password"
                    className="reset-password-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Conferma la nuova password"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>
              <button 
                type="submit" 
                className="reset-password-submit-btn"
                disabled={isLoading}
              >
                <span>{isLoading ? 'Reimpostazione in corso...' : 'Reimposta Password'}</span>
                <svg className="reset-password-submit-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="m12 5 7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </form>
            
            <div className="reset-password-footer">
              <Link to="/login" className="reset-password-back-link">
                ← Torna al Login
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ResetPassword;
